import type React from "react";
import { useState } from "react";
import { progressLogService } from "@/services/progressLogService";
import type { ProgressLog } from "@/types/progressLog";

interface ProgressFormProps {
	goalId?: string; // goalIdをオプショナルに変更
}

const ProgressForm: React.FC<ProgressFormProps> = ({ goalId }) => {
	const [progress, setProgress] = useState(0);
	const [note, setNote] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!goalId) {
			setError(new Error("目標IDが指定されていません"));
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const progressData: Omit<ProgressLog, "id" | "userId" | "createdAt"> = {
				goalId,
				progress,
				note: note.trim() || undefined, // 空文字列の場合はundefinedに
			};

			await progressLogService.createProgressLog(progressData);

			// フォームをリセット
			setProgress(0);
			setNote("");
		} catch (err) {
			setError(err as Error);
			console.error("進捗の記録に失敗しました:", err);
		} finally {
			setLoading(false);
		}
	};

	if (!goalId) {
		return <p className="text-red-500">目標IDが指定されていません</p>;
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			{error && (
				<div className="text-red-500 text-sm mb-4">
					エラーが発生しました: {error.message}
				</div>
			)}
			<div>
				<label htmlFor="progress" className="block mb-1">
					進捗 (%)
				</label>
				<input
					type="number"
					id="progress"
					value={progress}
					onChange={(e) => {
						const value = Number(e.target.value);
						if (value >= 0 && value <= 100) {
							setProgress(value);
						}
					}}
					min="0"
					max="100"
					className="w-full px-3 py-2 border rounded"
					required
				/>
			</div>
			<div>
				<label htmlFor="note" className="block mb-1">
					メモ
				</label>
				<textarea
					id="note"
					value={note}
					onChange={(e) => setNote(e.target.value)}
					className="w-full px-3 py-2 border rounded"
					rows={3}
					placeholder="進捗に関する補足情報を入力してください"
				/>
			</div>
			<button
				type="submit"
				className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
				disabled={loading}
			>
				{loading ? "記録中..." : "進捗を記録"}
			</button>
		</form>
	);
};

export default ProgressForm;
